/* eslint-disable */
// @ts-nocheck
import { useOpenCv } from '@src/lib/useOpenCv';
import { BoundingBox } from '@apiContract';

import { CropPointsCoordinates } from '@rdTypes/shared';
import { filterPointsCorrectly, getCornerArrayFromContour } from '@utils/openCvutils';

export const useOpenCvTools = () => {
  const { loaded, cv } = useOpenCv();

  const detectCorners = (image: HTMLImageElement): CropPointsCoordinates | null => {
    if (!cv) return null;
    // Reading image and building cv.Mat() class instance.
    const imageMat = cv.imread(image);

    // Converting source colorful image to grayscaled.
    cv.cvtColor(imageMat, imageMat, cv.COLOR_RGBA2GRAY, 0);

    // Converting grayscaled image to black and white binary image using thresholding.
    // Otsu's algorithm will automatically calculate threshold value - third argument in .threshold()
    cv.threshold(imageMat, imageMat, 0, 255, cv.THRESH_OTSU);

    // Looking for contours in the thresholded image.
    const contours = new cv.MatVector();
    const hierarchy = new cv.Mat();
    cv.findContours(imageMat, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

    // Approximating contours to find ones that are close to rectangles.
    const approximatedContours = new cv.MatVector();
    for (let i = 0; i < contours.size(); i++) {
      const mat = new cv.Mat();
      const contour = contours.get(i);
      const perimeter = cv.arcLength(contour, false);
      // Third argument here is extremely important. It controls how rough algorithm can be while approximating.
      cv.approxPolyDP(contour, mat, 0.05 * perimeter, true);
      approximatedContours.push_back(mat);
      mat.delete();
      contour.delete();
    }

    // Looking through the approximated contours to find rectangles. Picking rectangle with the biggest perimeter.
    let rectangleContour = null;
    const perimeterTreshold = (image.width + image.height) * 2 * 0.2; // Contour perimeter must be at least 20% from image perimeter.
    for (let i = 0; i < approximatedContours.size(); ++i) {
      const contour = approximatedContours.get(i);
      if (contour.rows === 4) {
        const perimeter = cv.arcLength(contour, false);
        if (perimeter > perimeterTreshold && (!rectangleContour || perimeter > cv.arcLength(rectangleContour, false))) {
          rectangleContour = contour;
        }
      }
    }

    if (!rectangleContour) return null;

    // TODO: Look for a simpler way to convert contour to coordinates.
    const cornerArray = getCornerArrayFromContour(cv, rectangleContour);
    const correctCorners = filterPointsCorrectly(cornerArray);

    imageMat.delete();
    contours.delete();
    hierarchy.delete();
    approximatedContours.delete();

    return {
      topLeft: {
        x: correctCorners[0].corner.x / image.width,
        y: correctCorners[0].corner.y / image.height,
      },
      topRight: {
        x: correctCorners[1].corner.x / image.width,
        y: correctCorners[1].corner.y / image.height,
      },
      bottomLeft: {
        x: correctCorners[3].corner.x / image.width,
        y: correctCorners[3].corner.y / image.height,
      },
      bottomRight: {
        x: correctCorners[2].corner.x / image.width,
        y: correctCorners[2].corner.y / image.height,
      },
    };
  };

  const transform = (
    imgElement: HTMLCanvasElement,
    naturalCropPoints: CropPointsCoordinates,
  ) => {
    const dst = cv.imread(imgElement);

    const bR = naturalCropPoints.bottomRight;
    const bL = naturalCropPoints.bottomLeft;
    const tR = naturalCropPoints.topRight;
    const tL = naturalCropPoints.topLeft;

    // create source coordinates matrix
    const sourceCoordinates = [tL, tR, bR, bL].map((point) => [point.x, point.y]);

    // get max width
    const maxWidth = Math.max(
      naturalCropPoints.bottomRight.x - naturalCropPoints.bottomLeft.x,
      naturalCropPoints.topRight.x - naturalCropPoints.topLeft.x,
    );
    // get max height
    const maxHeight = Math.max(
      naturalCropPoints.bottomLeft.y - naturalCropPoints.topLeft.y,
      naturalCropPoints.bottomRight.y - naturalCropPoints.topRight.y,
    );

    const destCoordinates = [
      [0, 0],
      [maxWidth - 1, 0],
      [maxWidth - 1, maxHeight - 1],
      [0, maxHeight - 1],
    ];

    // convert to open cv matrix objects
    const Ms = cv.matFromArray(4, 1, cv.CV_32FC2, [].concat(...(sourceCoordinates as any)));
    const Md = cv.matFromArray(4, 1, cv.CV_32FC2, [].concat(...(destCoordinates as any)));
    const transformMatrix = cv.getPerspectiveTransform(Ms, Md);

    // set new image size
    const dsize = new cv.Size(maxWidth, maxHeight);
    // perform warp
    cv.warpPerspective(dst, dst, transformMatrix, dsize, cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());

    const canvas = document.createElement('canvas');
    canvas.width = maxWidth;
    canvas.height = maxHeight;
    cv.imshow(canvas, dst);

    dst.delete();
    Ms.delete();
    Md.delete();
    transformMatrix.delete();

    return canvas.toDataURL();
  };

  const getTransformedInnerQuad = (
    cropBox: BoundingBox,
    innerBox: BoundingBox,
    imageResizeRatio: number = 1,
  ): BoundingBox => {
    const toPoints = (box: BoundingBox) => [
      { x: box.topLeftX, y: box.topLeftY },
      { x: box.topRightX, y: box.topRightY },
      { x: box.bottomRightX, y: box.bottomRightY },
      { x: box.bottomLeftX, y: box.bottomLeftY },
    ];

    const cropPoints = toPoints(cropBox).map((p) => ({
      x: p.x / imageResizeRatio,
      y: p.y / imageResizeRatio,
    }));

    const innerPoints = toPoints(innerBox).map((p) => ({
      x: p.x / imageResizeRatio,
      y: p.y / imageResizeRatio,
    }));

    const maxWidth = Math.max(cropPoints[1].x - cropPoints[0].x, cropPoints[2].x - cropPoints[3].x);
    const maxHeight = Math.max(cropPoints[3].y - cropPoints[0].y, cropPoints[2].y - cropPoints[1].y);

    const destPoints = [
      [0, 0],
      [maxWidth - 1, 0],
      [maxWidth - 1, maxHeight - 1],
      [0, maxHeight - 1],
    ];

    const Ms = cv.matFromArray(
      4,
      1,
      cv.CV_32FC2,
      cropPoints.flatMap((p) => [p.x, p.y]),
    );
    const Md = cv.matFromArray(4, 1, cv.CV_32FC2, destPoints.flat());
    const transformMatrix = cv.getPerspectiveTransform(Ms, Md);

    const innerMat = cv.matFromArray(
      4,
      1,
      cv.CV_32FC2,
      innerPoints.flatMap((p) => [p.x, p.y]),
    );
    const transformedMat = new cv.Mat();
    cv.perspectiveTransform(innerMat, transformedMat, transformMatrix);

    const data = transformedMat.data32F;
    const resultBox: BoundingBox = {
      id: innerBox.id,
      topLeftX: Math.floor(data[0]),
      topLeftY: Math.floor(data[1]),
      topRightX: Math.floor(data[2]),
      topRightY: Math.floor(data[3]),
      bottomRightX: Math.floor(data[4]),
      bottomRightY: Math.floor(data[5]),
      bottomLeftX: Math.floor(data[6]),
      bottomLeftY: Math.floor(data[7]),
    };

    // Cleanup
    Ms.delete();
    Md.delete();
    transformMatrix.delete();
    innerMat.delete();
    transformedMat.delete();

    return resultBox;
  };

  return { loaded, detectCorners, transform, getTransformedInnerQuad };
};
